<!--
  ClientDetailPage.vue -- Phase 2.3 Distributor-side Client workspace.

  Card: 2026-06-13 design bundle implementation -- https://trello.com/c/hYlwyKYT
  Pebble: Phase 2.3 Clients workspace (attention-first IA rethink).

  This page is a WORKSPACE, not a static profile. Six sections,
  organized around the Distributor <-> Client relationship:

    1. Header (name + status inline-editable, invite/login health, actions)
    1b. Client contact card (derived name, email, phone; inline edit)
    2. Stores  -- embedded list with status + order glance
    3. Pending approvals -- embedded mini-queue (F1.e endpoint with
       client_id filter)
    4. People -- accounts on the client side (read-only for MVP)
    5. Activity -- chronological event feed
    6. Notes -- freeform Distributor-side, auto-saving

  We do NOT collapse the link-out to the Approval Workspace into the
  embedded queue -- the embedded view is a triage glance ("what's hot
  on this Client right now"), and the Approval Workspace remains the
  full surface ("triage ALL approvals across the distributor").
-->
<template>
    <v-container fluid class="main-container">
        <div class="cdp" data-testid="client-detail-page">
            <div v-if="loading" class="cdp__loading">
                <v-progress-circular indeterminate color="primary" />
                <span>Loading...</span>
            </div>
            <div v-else-if="loadError" class="cdp__error">
                {{ loadError }}
            </div>
            <template v-else-if="client">
                <ClientWorkspaceHeader
                    :client="client"
                    :can-invite="canInvite"
                    :can-edit="canEdit"
                    :can-view-as-customer="canViewAsCustomer"
                    :impersonatable="client.impersonatable"
                    data-testid="client-detail-header"
                    @resend="onResend"
                    @revoke="onRevoke"
                    @view-as-customer="onViewAsCustomer"
                    @saved="onFieldSaved"
                />

                <p
                    v-if="viewAsError"
                    class="cdp__error cdp__error--inline"
                    role="alert"
                    data-testid="client-detail-view-as-error"
                >
                    {{ viewAsError }}
                </p>

                <!-- Client contact: name (derived, read-only), email, phone. -->
                <section class="cdp__section" data-testid="client-detail-contact">
                    <div class="d-flex align-center">
                        <h3 class="cdp__section-title">Client contact</h3>
                        <HelpIcon
                            audience="owner"
                            slug="manage-clients"
                            heading="open-a-client"
                            v-if="!isSuperAdminSurface"
                        />
                    </div>
                    <ClientContactCard
                        :client-id="clientId"
                        :client="client"
                        :contact="contact"
                        :can-edit="canEdit"
                        @saved="onFieldSaved"
                    />
                </section>

                <section class="cdp__section">
                    <div class="d-flex align-center">
                        <h3 class="cdp__section-title">Stores</h3>
                        <HelpIcon
                            audience="owner"
                            slug="manage-clients"
                            heading="link-a-client-to-a-store-or-remove-it"
                            v-if="!isSuperAdminSurface"
                        />
                    </div>
                    <ClientStoresPanel
                        :client-id="clientId"
                        data-testid="client-detail-stores"
                    />
                </section>

                <!--
                  Client Portal v2 (PR B): client-level departments + budgets +
                  fiscal config. Distributor-only config (decision 5) -- gated on
                  canEdit (client.update) so Client-tier viewers do not see the
                  write surface.
                -->
                <section class="cdp__section" v-if="canEdit && enableDepartments">
                    <ClientDepartmentsPanel
                        :client-id="clientId"
                        data-testid="client-detail-departments"
                    />
                </section>

                <section class="cdp__section">
                    <header class="cdp__section-head">
                        <h3 class="cdp__section-title">
                            Pending approvals
                        </h3>
                        <router-link
                            v-if="!isSuperAdminSurface"
                            class="cdp__section-link"
                            :to="`/distributor/decorations/approvals?client_id=${clientId}`"
                            data-testid="client-detail-approvals-link"
                        >
                            See all in workspace ->
                        </router-link>
                    </header>
                    <ClientApprovalsPanel
                        :client-id="clientId"
                        :link-rows="!isSuperAdminSurface"
                        data-testid="client-detail-approvals"
                    />
                </section>

                <!--
                  Company shipping address, edited here (it used to live on the
                  separate edit page). It is NOT auto-saved: saving validates
                  the address with ShipEngine and asks the user to accept the
                  normalized one (ShippingAddressEditor's own Save).
                -->
                <section class="cdp__section" data-testid="client-detail-shipping">
                    <h3 class="cdp__section-title">Shipping address</h3>
                    <ShippingAddressEditor
                        :endpoint="`/clients/${clientId}/shipping-address`"
                        label="Company shipping address"
                        :disabled="!canEdit"
                        data-testid="client-detail-shipping-editor"
                    />
                </section>

                <section class="cdp__section">
                    <h3 class="cdp__section-title">People</h3>
                    <p class="cdp__section-note">
                        Everyone this client has on their side: name, role, email and phone.
                    </p>
                    <ClientPeoplePanel
                        :client-id="clientId"
                        data-testid="client-detail-people"
                    />
                </section>

                <section class="cdp__section">
                    <h3 class="cdp__section-title">Activity</h3>
                    <ClientActivityStream
                        :client-id="clientId"
                        data-testid="client-detail-activity"
                    />
                </section>

                <section class="cdp__section" v-if="canEdit">
                    <h3 class="cdp__section-title">Notes</h3>
                    <ClientNotesField
                        :client-id="clientId"
                        :initial-value="client.notes || ''"
                        data-testid="client-detail-notes"
                    />
                </section>
            </template>
        </div>
    </v-container>
</template>

<script>
import { useClientsWorkspaceStore } from '@/stores/clientsWorkspace'
import { useImpersonationStore } from '@/stores/impersonation'
import api from '@/services/api'
import ClientWorkspaceHeader from './ClientWorkspaceHeader.vue'
import ClientStoresPanel from './ClientStoresPanel.vue'
import ClientApprovalsPanel from './ClientApprovalsPanel.vue'
import ClientPeoplePanel from './ClientPeoplePanel.vue'
import ClientContactCard from './ClientContactCard.vue'
import ShippingAddressEditor from './ShippingAddressEditor.vue'
import ClientActivityStream from './ClientActivityStream.vue'
import ClientNotesField from './ClientNotesField.vue'
import ClientDepartmentsPanel from './ClientDepartmentsPanel.vue'
import { useEnableDepartments } from '@/composables/useEnableDepartments'
import HelpIcon from '@/components/Help/HelpIcon.vue'

export default {
    name: 'ClientDetailPage',
    components: {
        HelpIcon,
        ClientWorkspaceHeader,
        ClientStoresPanel,
        ClientApprovalsPanel,
        ClientPeoplePanel,
        ClientContactCard,
        ShippingAddressEditor,
        ClientActivityStream,
        ClientNotesField,
        ClientDepartmentsPanel,
    },
    setup() {
        // Departments feature gate (SA-global), read through the ONE
        // shared composable. Defaults TRUE so the panel does not flicker
        // out while the GET is in flight.
        const { enableDepartments } = useEnableDepartments()
        return { enableDepartments }
    },
    inject: {
        distributorLayoutCapabilities: { default: () => [] },
    },
    props: {
        id: { type: [String, Number], required: true },
        // Which shell hosts the page. 'superadmin' is the SA client details
        // route /admin/clients/:id (Jake 2026-10-27: reuse this workspace for
        // the SA, fully editable). There the page renders nothing that routes
        // into the /distributor layout -- that layout has no /admin escape
        // and traps the SA (https://trello.com/c/0SeIzpYd) -- and "View as
        // client" is left to the SA list's Impersonate action.
        surface: {
            type: String,
            default: 'distributor',
            validator: (v) => ['distributor', 'superadmin'].includes(v),
        },
    },
    data() {
        return {
            client: null,
            // GET /clients/:id/contact -- derived name + portal flag.
            contact: null,
            loading: true,
            loadError: '',
            // A refused "View as client" start (inline, page stays).
            viewAsError: '',
            // The detail page reads the full Client row (incl. notes
            // + invitation rows) directly from /api/clients/:id +
            // /api/clients/:id/invitations rather than the summary
            // rollup -- the summary skips the notes field (large, not
            // needed on the list page) and the invitation history.
            invitations: [],
        }
    },
    computed: {
        store() {
            return useClientsWorkspaceStore()
        },
        capabilities() {
            const inj = this.distributorLayoutCapabilities
            if (Array.isArray(inj)) return inj
            if (inj && typeof inj === 'object' && Array.isArray(inj.value)) {
                return inj.value
            }
            return []
        },
        canEdit() {
            return this.capabilities.includes('client.update')
        },
        canInvite() {
            return this.capabilities.includes('client.invite_admin')
        },
        // "View as client" gate: caller holds
        // distributor.impersonate_customer (Owner / Builder / Sales
        // Executive). The backend re-enforces ownership (404) + the
        // portal-capable/inactive gates (422).
        canViewAsCustomer() {
            if (this.isSuperAdminSurface) return false
            return this.capabilities.includes(
                'distributor.impersonate_customer',
            )
        },
        clientId() {
            return Number(this.id)
        },
        isSuperAdminSurface() {
            return this.surface === 'superadmin'
        },
    },
    watch: {
        id() {
            this.loadClient()
        },
    },
    mounted() {
        this.loadClient()
    },
    methods: {
        // ``silent`` re-reads after an inline save without flashing the
        // loading state (which would unmount every inline editor mid-edit).
        async loadClient({ silent = false } = {}) {
            if (!silent) this.loading = true
            this.loadError = ''
            try {
                // The SAFRS /clients/<id> surface does not carry the
                // ``impersonatable`` flag (the list page gets it from the
                // summary rollup). Fetch it alongside so the header can
                // disable-with-tooltip the "View as client" button in
                // parity with the list page, instead of letting the click
                // hit the backend 422 and surface as an error banner. The
                // fetch is fault-tolerant: a failure leaves the flag
                // undefined -> the button falls back to disabled (safe).
                const [clientRes, invRes, imperRes, contactRes] = await Promise.all([
                    api.get(`/clients/${this.clientId}`),
                    api.get(`/clients/${this.clientId}/invitations`),
                    api
                        .get(`/clients/${this.clientId}/impersonatable`)
                        .catch(() => null),
                    api
                        .get(`/clients/${this.clientId}/contact`)
                        .catch(() => null),
                ])
                this.contact = contactRes?.data || null
                const data = clientRes?.data?.data
                if (data && data.attributes) {
                    this.client = { id: data.id, ...data.attributes }
                } else if (data) {
                    this.client = data
                } else {
                    this.client = null
                }
                if (this.client) {
                    this.client = {
                        ...this.client,
                        impersonatable: Boolean(
                            imperRes?.data?.impersonatable,
                        ),
                    }
                }
                this.invitations = Array.isArray(invRes?.data?.invitations)
                    ? invRes.data.invitations
                    : []
                // Attach the current invitation block to the client so
                // the header can render "Invited X days ago" without a
                // separate prop drill. The most-recent row by id is the
                // active state of the chain (mirrors the backend
                // ``_derive_invitation_status`` logic).
                if (this.invitations.length > 0) {
                    const latest = this.invitations.reduce((a, b) =>
                        (a.id || 0) > (b.id || 0) ? a : b,
                    )
                    this.client = {
                        ...this.client,
                        latest_invitation: latest,
                    }
                }
            } catch (e) {
                this.loadError = e?.response?.status === 404
                    ? 'Client not found.'
                    : 'Could not load this client.'
            } finally {
                this.loading = false
            }
        },
        async onResend() {
            if (!this.client) return
            const latest = this.client.latest_invitation
            const email = latest?.invited_email || this.client.email
            if (!email) {
                this.loadError = 'No contact email on record to resend to.'
                return
            }
            try {
                await this.store.resendInvitation(
                    this.clientId,
                    email,
                    latest?.invited_first_name,
                    latest?.invited_last_name,
                )
                await this.loadClient()
            } catch (e) {
                this.loadError =
                    e?.response?.data?.message || 'Could not resend.'
            }
        },
        async onRevoke() {
            if (!this.client?.latest_invitation) return
            try {
                await this.store.revokeInvitation(
                    this.clientId,
                    this.client.latest_invitation.id,
                )
                await this.loadClient()
            } catch (e) {
                this.loadError =
                    e?.response?.data?.message || 'Could not revoke.'
            }
        },
        // An inline save landed. The email is the portal login identity, so
        // a change can re-issue the invite / move the account: re-read the
        // derived state (invitation health, portal flag) quietly. Name,
        // phone and status have no derived fallout.
        onFieldSaved(evt) {
            if (evt && evt.field === 'email') this.loadClient({ silent: true })
            else if (this.client && evt) {
                this.client = { ...this.client, [evt.field]: evt.value }
            }
        },
        // "View as client": start an ownership-scoped client impersonation
        // and navigate to the Client Portal. The store flips to client mode
        // with an exit target of /distributor/clients. A failed start
        // (backend 404 cross-tenant / 422 gates) leaves the store untouched
        // and surfaces the message on the page. Card
        // https://trello.com/c/k8m2iLzG.
        async onViewAsCustomer() {
            if (!this.client) return
            this.viewAsError = ''
            try {
                await useImpersonationStore().startViewAsCustomer(
                    this.clientId,
                    this.client.name,
                )
                if (typeof window !== 'undefined' && window.location) {
                    window.location.assign('/portal')
                }
            } catch (e) {
                // Inline, NOT loadError: loadError replaces the whole page,
                // which would hide the client the user is looking at.
                this.viewAsError =
                    e?.response?.data?.message ||
                    'Could not view as this client.'
            }
        },
    },
}
</script>

<style scoped>
.cdp {
    padding: 16px;
    display: flex;
    flex-direction: column;
    gap: 24px;
}
.cdp__loading,
.cdp__error {
    padding: 32px;
    text-align: center;
}
.cdp__error {
    color: #c0392b;
}
.cdp__error--inline {
    margin: 0;
    padding: 0;
    text-align: left;
}
.cdp__section {
    background: var(--tf-bg, #fff);
    border: 1px solid var(--tf-border, #d0d4dc);
    border-radius: var(--tf-radius-sm, 6px);
    padding: 16px;
}
.cdp__section-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
}
.cdp__section-title {
    margin: 0 0 12px;
    font-size: 14px;
    font-weight: 600;
    color: var(--tf-fg-2);
    text-transform: uppercase;
    letter-spacing: 0.05em;
}
.cdp__section-head .cdp__section-title {
    margin: 0;
}
.cdp__section-note {
    margin: -6px 0 10px;
    font-size: 12px;
    color: var(--tf-fg-3, #6b7280);
}
.cdp__section-link {
    font-size: 13px;
    color: var(--tf-accent, #2c5fff);
    text-decoration: none;
}
.cdp__section-link:hover {
    text-decoration: underline;
}
</style>
